/**
 * Master/Sub-Account API Integration Layer
 * 
 * This service layer provides a clean interface for all master/sub-account
 * related API operations, following the implementation guide specifications.
 */

import axios, { AxiosResponse, AxiosError } from 'axios';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface SubAccountCredentials {
  username: string;
  password: string;
}

export interface CreateSubAccountPayload {
  subAccountPassword: string;
  subAccountName: string;
  company: string;
}

export interface ChangeSubAccountPasswordPayload {
  targetSubAccountUID: string;
  newPassword: string;
}

export interface MoveSmartProbePayload {
  probeId: string;
  fromTeamName: string;
  toTeamName: string;
  probeType: 'trap' | 'rodent';
}

export interface SubAccount {
  uid: string;
  username: string;
  email: string | null;
  company: string;
  teamName: string[];
  role: string;
  isSubAccount: true;
  deactivated: boolean;
  parentMasterUID: string;
  parentMasterEmail: string;
  fahrenheit: boolean;
  insectCountThresholds: {
    yellow: number;
    lightRed: number;
    darkRed: number;
  };
  filterOptions: {
    filter: string;
    ascend: boolean;
  };
  notification: boolean;
  emailNotiDays: {
    Monday: boolean;
    Tuesday: boolean;
    Wednesday: boolean;
    Thursday: boolean;
    Friday: boolean;
    Saturday: boolean;
    Sunday: boolean;
  };
  emailList: string[];
  savedSSIDs: string[];
  latestRodentWakeUpTime: string;
  createdAt: string;
  updatedAt: string;
  accountAgeDays: number;
}

export interface MasterAccountInfo {
  masterAccount: {
    email: string;
    company: string;
    teams?: string[];
    teamName?: string | string[]; // API provides teamName (array or string), teams is fallback
    uid?: string;
  };
  subAccounts: SubAccount[];
}

export interface EnhancedUserInfo {
  username: string;
  email: string;
  company: string;
  teams: string[];
  accountType: 'master' | 'sub' | 'regular';
  isMaster: boolean;
  isSubAccount: boolean;
  parentMasterUID?: string;
  parentMasterEmail?: string;
  deactivated?: boolean;
  masterAccountInfo?: MasterAccountInfo;
  // Include existing user info fields
  role: string;
  fahrenheit: boolean;
  yellow: number;
  lightRed: number;
  darkRed: number;
  filter: string;
  ascend: boolean;
  savedSSIDs: string[];
  notification: boolean;
  emailNotiDays: {
    Monday: boolean;
    Tuesday: boolean;
    Wednesday: boolean;
    Thursday: boolean;
    Friday: boolean;
    Saturday: boolean;
    Sunday: boolean;
  };
  emailList: string[];
  monthListSinceJoined: string[];
  probeListFormatted: {
    probes: { probeId: string }[];
  };
  probeList: string[];
}

export interface TeamProbesResponse {
  teams: {
    teamsName: string;
    owner: string;
    traps: Array<{
      trapId: string;
      trapName: string;
      building: string;
      location: string;
      assignedTeam: string;
      lastestImageDate: string;
      wifiInfo: number;
      batteryInfo: number;
      particleCount: number;
    }>;
  };
}

// ============================================================================
// API RESPONSE INTERFACES
// ============================================================================

export interface SubAccountLoginResponse {
  success: boolean;
  user: {
    uid: string;
    username: string;
    name: string;
    company: string;
    teams: string[];
    token: string;
    isSubAccount: true;
    parentMasterEmail: string;
    parentMasterUID: string;
  };
}

export interface CreateSubAccountResponse {
  success: boolean;
  message: string;
  subAccount: SubAccount;
  createdTeam: {
    teamsName: string;
    teamsId: string;
    owner: string;
  };
}

export interface MySubAccountsResponse {
  success: boolean;
  masterAccount: {
    email: string;
    company: string;
    teams: string[];
  };
  subAccounts: SubAccount[];
  totalSubAccounts: number;
}

export interface ToggleSubAccountResponse {
  success: boolean;
  message: string;
  subAccount: {
    uid: string;
    username: string;
    deactivated: boolean;
  };
}

export interface MoveSmartProbeResponse {
  success: boolean;
  message: string;
  probe: {
    id: string;
    type: string;
    previousTeam: string;
    newTeam: string;
  };
  teams: {
    fromTeam: {
      name: string;
      remainingProbes: number;
    };
    toTeam: {
      name: string;
      totalProbes: number;
    };
  };
}

export interface EnhancedUserInfoResponse {
  userInfo: EnhancedUserInfo;
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

export class MasterSubAccountApiError extends Error {
  public status?: number;
  public code?: string;
  public details?: any;

  constructor(message: string, status?: number, code?: string, details?: any) {
    super(message);
    this.name = 'MasterSubAccountApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const handleApiError = (error: AxiosError): never => {
  const status = error.response?.status;
  const data = error.response?.data as any;
  
  let message = 'An unexpected error occurred';
  let code = 'UNKNOWN_ERROR';
  
  if (status === 400) {
    message = data?.message || 'Invalid request data';
    code = 'BAD_REQUEST';
  } else if (status === 401) {
    message = data?.message || 'Authentication failed';
    code = 'UNAUTHORIZED';
  } else if (status === 403) {
    message = data?.message || 'Access denied';
    code = 'FORBIDDEN';
  } else if (status === 404) {
    message = data?.message || 'Resource not found';
    code = 'NOT_FOUND';
  } else if (status === 409) {
    message = data?.message || 'Resource conflict';
    code = 'CONFLICT';
  } else if (status && status >= 500) {
    message = 'Server error. Please try again later.';
    code = 'SERVER_ERROR';
  } else if (error.code === 'NETWORK_ERROR') {
    message = 'Network error. Please check your connection.';
    code = 'NETWORK_ERROR';
  }
  
  throw new MasterSubAccountApiError(message, status, code, data);
};

// ============================================================================
// API HELPER FUNCTIONS
// ============================================================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('GrainGuardianToken');
  if (!token) {
    throw new MasterSubAccountApiError('No authentication token found', 401, 'NO_TOKEN');
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const apiRequest = async <T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  endpoint: string,
  data?: any
): Promise<T> => {
  try {
    const config = {
      method,
      url: endpoint,
      headers: getAuthHeaders(),
      ...(data && { data })
    };

    const response: AxiosResponse<T> = await axios(config);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleApiError(error);
    }
    throw error;
  }
};

// ============================================================================
// AUTHENTICATION ENDPOINTS
// ============================================================================

export const authApi = {
  /**
   * Sub-account login
   * @param credentials Username and password for sub-account
   * @returns Sub-account user data with token
   */
  subAccountLogin: async (credentials: SubAccountCredentials): Promise<SubAccountLoginResponse> => {
    try {
      const response = await axios.post<SubAccountLoginResponse>('/api/master/subLogin', credentials);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        handleApiError(error);
      }
      throw error;
    }
  },

  /**
   * Get enhanced user information including account type and master/sub-account data
   * @returns Enhanced user information
   */
  getEnhancedUserInfo: async (): Promise<EnhancedUserInfoResponse> => {
    return apiRequest<EnhancedUserInfoResponse>('GET', '/api/users/');
  }
};

// ============================================================================
// MASTER ACCOUNT MANAGEMENT ENDPOINTS
// ============================================================================

export const masterAccountApi = {
  /**
   * Create a new sub-account
   * @param payload Sub-account creation data
   * @returns Created sub-account information
   */
  createSubAccount: async (payload: CreateSubAccountPayload): Promise<CreateSubAccountResponse> => {
    return apiRequest<CreateSubAccountResponse>('POST', '/api/master/createSubAccount', payload);
  },

  /**
   * Get all sub-accounts for the current master account
   * @returns List of sub-accounts and master account info
   */
  getMySubAccounts: async (): Promise<MySubAccountsResponse> => {
    return apiRequest<MySubAccountsResponse>('GET', '/api/master/mySubAccounts');
  },

  /**
   * Toggle sub-account active/inactive status
   * @param subAccountUID UID of the sub-account to toggle
   * @returns Updated sub-account status
   */
  toggleSubAccountStatus: async (subAccountUID: string): Promise<ToggleSubAccountResponse> => {
    return apiRequest<ToggleSubAccountResponse>('POST', '/api/master/toggleSubStatus', {
      subAccountUID
    });
  },

  /**
   * Change sub-account password
   * @param payload Target sub-account UID and new password
   * @returns Success confirmation
   */
  changeSubAccountPassword: async (payload: ChangeSubAccountPasswordPayload): Promise<{ success: boolean; message: string }> => {
    return apiRequest<{ success: boolean; message: string }>('POST', '/api/master/changeSubAccountPassword', payload);
  },

  /**
   * Delete a sub-account
   * @param subAccountUID UID of the sub-account to delete
   * @returns Deletion confirmation with moved probes count
   */
  deleteSubAccount: async (subAccountUID: string): Promise<{
    success: boolean;
    message: string;
    trapsMovedBack: number;
    teamDeleted: string;
  }> => {
    return apiRequest<{
      success: boolean;
      message: string;
      trapsMovedBack: number;
      teamDeleted: string;
    }>('DELETE', '/api/master/deleteSubAccount', { subAccountUID });
  }
};

// ============================================================================
// SMARTPROBE MANAGEMENT ENDPOINTS
// ============================================================================

export const smartProbeApi = {
  /**
   * Move a SmartProbe between teams
   * @param payload Move operation details
   * @returns Move operation result
   */
  moveSmartProbe: async (payload: MoveSmartProbePayload): Promise<MoveSmartProbeResponse> => {
    return apiRequest<MoveSmartProbeResponse>('POST', '/api/master/moveSmartProbe', payload);
  },

  /**
   * Get all probes for a specific team
   * @param teamName Name of the team
   * @returns Team probes data
   */
  getTeamProbes: async (teamName: string): Promise<TeamProbesResponse> => {
    return apiRequest<TeamProbesResponse>('POST', '/api/teams/getTeam', {
      name: teamName
    });
  }
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

export const validationHelpers = {
  /**
   * Validate password strength for sub-account creation
   * @param password Password to validate
   * @returns Validation result with details
   */
  validatePassword: (password: string): {
    isValid: boolean;
    errors: string[];
    strength: 'weak' | 'medium' | 'strong';
  } => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*]/.test(password)) {
      errors.push('Password must contain at least one special character (!@#$%^&*)');
    }
    
    const isValid = errors.length === 0;
    let strength: 'weak' | 'medium' | 'strong' = 'weak';
    
    if (isValid) {
      if (password.length >= 12 && /[!@#$%^&*].*[!@#$%^&*]/.test(password)) {
        strength = 'strong';
      } else if (password.length >= 10) {
        strength = 'medium';
      }
    }
    
    return { isValid, errors, strength };
  },

  /**
   * Validate sub-account name
   * @param name Sub-account name to validate
   * @returns Validation result
   */
  validateSubAccountName: (name: string): {
    isValid: boolean;
    error?: string;
  } => {
    if (!name || name.trim().length === 0) {
      return { isValid: false, error: 'Sub-account name is required' };
    }
    
    if (name.trim().length < 3) {
      return { isValid: false, error: 'Sub-account name must be at least 3 characters long' };
    }
    
    if (name.trim().length > 50) {
      return { isValid: false, error: 'Sub-account name must be less than 50 characters' };
    }
    
    // Check for invalid characters (optional - adjust based on your requirements)
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) {
      return { isValid: false, error: 'Sub-account name can only contain letters, numbers, spaces, hyphens, and underscores' };
    }
    
    return { isValid: true };
  },

  /**
   * Validate company name
   * @param company Company name to validate
   * @returns Validation result
   */
  validateCompanyName: (company: string): {
    isValid: boolean;
    error?: string;
  } => {
    if (!company || company.trim().length === 0) {
      return { isValid: false, error: 'Company name is required' };
    }
    
    if (company.trim().length < 2) {
      return { isValid: false, error: 'Company name must be at least 2 characters long' };
    }
    
    if (company.trim().length > 100) {
      return { isValid: false, error: 'Company name must be less than 100 characters' };
    }
    
    return { isValid: true };
  }
};

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

export const permissionHelpers = {
  /**
   * Check if user has specific permission
   * @param user User object with account type info
   * @param permission Permission to check
   * @returns Whether user has permission
   */
  checkPermission: (
    user: { isSubAccount: boolean; isMasterAccount: boolean; accountType: string },
    permission: string
  ): boolean => {
    const permissions = {
      'edit_smartprobe': !user.isSubAccount,
      'delete_data': !user.isSubAccount,
      'manage_teams': user.isMasterAccount,
      'create_subaccount': user.isMasterAccount,
      'move_smartprobe': user.isMasterAccount,
      'access_connect': !user.isSubAccount,
      'bulk_operations': !user.isSubAccount,
      'access_settings': !user.isSubAccount,
      'edit_insect_counts': !user.isSubAccount,
      'delete_snapshots': !user.isSubAccount,
      'change_probe_settings': !user.isSubAccount,
    };
    
    return (permissions as Record<string, boolean>)[permission] || false;
  },

  /**
   * Get all permissions for a user
   * @param user User object with account type info
   * @returns Object with all permissions
   */
  getUserPermissions: (user: { isSubAccount: boolean; isMasterAccount: boolean; accountType: string }) => {
    return {
      canEditSmartProbe: !user.isSubAccount,
      canDeleteData: !user.isSubAccount,
      canManageTeams: user.isMasterAccount,
      canCreateSubAccount: user.isMasterAccount,
      canMoveSmartProbe: user.isMasterAccount,
      canAccessConnect: !user.isSubAccount,
      canUseBulkOperations: !user.isSubAccount,
      canAccessSettings: !user.isSubAccount,
      canEditInsectCounts: !user.isSubAccount,
      canDeleteSnapshots: !user.isSubAccount,
      canChangeProbeSettings: !user.isSubAccount,
    };
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const utilityHelpers = {
  /**
   * Format team display name for UI
   * @param teamName Raw team name from API
   * @param subAccounts List of sub-accounts for context
   * @returns Formatted display name
   */
  formatTeamDisplayName: (teamName: string, subAccounts?: SubAccount[]): string => {
    if (!teamName) return 'Unknown Team';
    
    // Check if it's a sub-account team (format: username#number)
    const subAccountMatch = teamName.match(/^(.+)#\d+$/);
    if (subAccountMatch && subAccounts) {
      const subAccount = subAccounts.find(sub => 
        sub.teamName.includes(teamName)
      );
      if (subAccount) {
        return `${subAccount.username} (${subAccount.company})`;
      }
      return subAccountMatch[1];
    }
    
    return teamName;
  },

  /**
   * Generate secure password suggestion
   * @returns Suggested secure password
   */
  generateSecurePassword: (): string => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*';
    
    let password = '';
    
    // Ensure at least one character from each category
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest randomly
    const allChars = uppercase + lowercase + numbers + symbols;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  },

  /**
   * Format date for display
   * @param dateString ISO date string
   * @returns Formatted date string
   */
  formatDate: (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  },

  /**
   * Calculate account age
   * @param createdAt Account creation date
   * @returns Human-readable age string
   */
  getAccountAge: (createdAt: string): string => {
    try {
      const created = new Date(createdAt);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - created.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 7) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} month${months !== 1 ? 's' : ''} ago`;
      } else {
        const years = Math.floor(diffDays / 365);
        return `${years} year${years !== 1 ? 's' : ''} ago`;
      }
    } catch {
      return 'Unknown';
    }
  }
};

// ============================================================================
// EXPORT ALL APIs
// ============================================================================

export default {
  auth: authApi,
  masterAccount: masterAccountApi,
  smartProbe: smartProbeApi,
  validation: validationHelpers,
  permissions: permissionHelpers,
  utils: utilityHelpers,
  errors: {
    MasterSubAccountApiError
  }
};


